import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
export const useChatMessageStore = create()(persist((set, get)=>({
        messages: {},
        reactions: {},
        typingUsers: {},
        addMessage: (roomId, message)=>set((state)=>{
                const existingMessages = state.messages[roomId] || [];
                const isDuplicate = existingMessages.some((msg)=>msg.id === message.id);
                if (isDuplicate) {
                    return state;
                }
                const newMessages = [
                    ...existingMessages,
                    message
                ];
                // Sort messages by createdAt timestamp
                newMessages.sort((a, b)=>{
                    const dateA = new Date(a.createdAt).getTime();
                    const dateB = new Date(b.createdAt).getTime();
                    return dateA - dateB;
                });
                return {
                    messages: {
                        ...state.messages,
                        [roomId]: newMessages
                    }
                };
            }),
        removeMessage: (roomId, messageId)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).filter((msg)=>msg.id !== messageId)
                    }
                })),
        updateMessageStatus: (roomId, messageId, status, failureReason)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).map((msg)=>msg.id === messageId ? {
                                ...msg,
                                status,
                                failureReason
                            } : msg)
                    }
                })),
        markMessageAsRead: (roomId, messageId)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).map((msg)=>msg.id === messageId ? {
                                ...msg,
                                isRead: true
                            } : msg)
                    }
                })),
        markAllMessagesAsRead: (roomId)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).map((msg)=>({
                                ...msg,
                                isRead: true
                            }))
                    }
                })),
        markAllAsRead: (roomId)=>{
            // Alias for markAllMessagesAsRead
            const state = get();
            state.markAllMessagesAsRead(roomId);
        },
        addReaction: (roomId, reaction)=>set((state)=>{
                const roomReactions = state.reactions[roomId] || {};
                const messageReactions = roomReactions[reaction.messageId] || [];
                // Check for duplicate reactions
                const isDuplicate = messageReactions.some((r)=>r.id === reaction.id || r.userId === reaction.userId && r.reactionType === reaction.reactionType);
                if (isDuplicate) {
                    return state;
                }
                return {
                    reactions: {
                        ...state.reactions,
                        [roomId]: {
                            ...roomReactions,
                            [reaction.messageId]: [
                                ...messageReactions,
                                reaction
                            ]
                        }
                    }
                };
            }),
        removeReaction: (roomId, messageId, userId, reactionType)=>set((state)=>{
                const roomReactions = state.reactions[roomId] || {};
                const messageReactions = roomReactions[messageId.toString()] || [];
                return {
                    reactions: {
                        ...state.reactions,
                        [roomId]: {
                            ...roomReactions,
                            [messageId.toString()]: messageReactions.filter((r)=>!(r.userId === userId && r.reactionType === reactionType))
                        }
                    }
                };
            }),
        setTypingUsers: (roomId, userIds)=>set((state)=>({
                    typingUsers: {
                        ...state.typingUsers,
                        [roomId]: userIds
                    }
                })),
        addTypingUser: (roomId, userId)=>set((state)=>{
                const currentUsers = state.typingUsers[roomId] || [];
                if (currentUsers.includes(userId)) return state;
                return {
                    typingUsers: {
                        ...state.typingUsers,
                        [roomId]: [
                            ...currentUsers,
                            userId
                        ]
                    }
                };
            }),
        removeTypingUser: (roomId, userId)=>set((state)=>({
                    typingUsers: {
                        ...state.typingUsers,
                        [roomId]: (state.typingUsers[roomId] || []).filter((id)=>id !== userId)
                    }
                })),
        setTypingUser: (roomId, userId, isTyping)=>{
            if (isTyping) {
                get().addTypingUser(roomId, userId);
            } else {
                get().removeTypingUser(roomId, userId);
            }
        },
        clearMessages: (roomId)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: []
                    },
                    reactions: {
                        ...state.reactions,
                        [roomId]: {}
                    },
                    typingUsers: {
                        ...state.typingUsers,
                        [roomId]: []
                    }
                })),
        clearRoom: (roomId)=>{
            // Alias for clearMessages
            get().clearMessages(roomId);
        },
        updateMessage: (roomId, messageId, updates)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).map((msg)=>msg.id === messageId ? {
                                ...msg,
                                ...updates
                            } : msg)
                    }
                })),
        getUnreadCount: (roomId)=>{
            const state = get();
            const roomMessages = state.messages[roomId] || [];
            return roomMessages.filter((msg)=>!msg.isRead).length;
        },
        getLastMessage: (roomId)=>{
            const state = get();
            const roomMessages = state.messages[roomId] || [];
            return roomMessages.length > 0 ? roomMessages[roomMessages.length - 1] : null;
        },
        retryFailedMessage: (roomId, messageId)=>set((state)=>({
                    messages: {
                        ...state.messages,
                        [roomId]: (state.messages[roomId] || []).map((msg)=>msg.id === messageId ? {
                                ...msg,
                                status: 'sending',
                                failureReason: undefined
                            } : msg)
                    }
                }))
    }), {
    name: 'chat-message-storage',
    storage: createJSONStorage(()=>localStorage),
    partialize: (state)=>({
            messages: state.messages,
            reactions: state.reactions
        })
}));
